<template>
  <v-card :loading="loading" :disabled="loading" class="student-exam-timetable">
    <div class="hero-panel">
      <v-card-title class="d-flex flex-wrap align-start hero-header">
        <div class="hero-copy">
          <div class="text-h5 font-weight-bold hero-title">
            {{ $t("student_exam_timetable.title") }}
          </div>
          <div class="text-body-2 hero-subtitle mt-2">
            {{ $t("student_exam_timetable.subtitle") }}
          </div>
        </div>

        <v-spacer></v-spacer>
      </v-card-title>
    </div>

    <v-card-text class="content-shell">
      <v-sheet class="filter-panel pa-4 mb-4">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="selectedTerm"
              :items="termOptions"
              item-text="label"
              item-value="value"
              filled
              rounded
              dense
              hide-details
              background-color="white"
              color="deep-orange lighten-1"
              prepend-inner-icon="mdi-calendar-month-outline"
              class="soft-field"
              :label="$t('student_exam_timetable.year_semester')"
              @change="onTermChange"
            ></v-select>
          </v-col>
          <v-col v-if="canEditStudentId" cols="12" sm="6" md="3">
            <v-text-field
              v-model="studentIdInput"
              filled
              rounded
              dense
              hide-details
              background-color="white"
              color="deep-orange lighten-1"
              prepend-inner-icon="mdi-card-account-details-outline"
              class="soft-field"
              :label="$t('student_exam_timetable.student_id')"
              @keyup.enter="fetchData"
            ></v-text-field>
          </v-col>
          <v-col
            v-if="canEditStudentId"
            cols="12"
            sm="6"
            md="2"
            class="d-flex justify-center justify-sm-start justify-md-center"
          >
            <v-btn
              color="deep-orange lighten-1"
              class="white--text search-btn"
              @click="fetchData"
            >
              <v-icon left>mdi-magnify</v-icon>
              {{ $t("teach_table.search") }}
            </v-btn>
          </v-col>
          <v-col v-if="examRows.length > 0" cols="12" md="4" class="d-flex justify-end updated-at-col">
            <v-chip small class="meta-chip" color="orange lighten-5" text-color="brown darken-1">
              <v-icon left small>mdi-update</v-icon>
              {{ $t("student_exam_timetable.updated_at") }}: {{ formattedUpdatedAt }}
            </v-chip>
          </v-col>
        </v-row>

        <v-row dense class="mt-1">
          <v-col cols="12" class="chip-row d-flex flex-wrap">
            <v-chip
              small
              class="meta-chip mr-2 mb-2"
              color="amber lighten-5"
              text-color="deep-orange darken-2"
            >
              <v-icon left small>mdi-calendar-range</v-icon>
              {{ $t("student_exam_timetable.year_semester") }}: {{ selectedTermLabel }}
            </v-chip>
            <v-chip
              v-if="studentId"
              small
              class="meta-chip mr-2 mb-2"
              color="light-blue lighten-5"
              text-color="blue-grey darken-2"
            >
              <v-icon left small>mdi-account-badge-outline</v-icon>
              {{ $t("student_exam_timetable.student_id") }}: {{ studentId }}
            </v-chip>
            <div v-if="examRows.length > 0" class="download-actions">
              <v-btn
                small
                outlined
                color="deep-orange"
                class="download-action mr-2 mb-2"
                elevation="3"
                :loading="downloadingType === 'jpg'"
                :disabled="!!downloadingType"
                @click="downloadExamTimetable('jpg')"
              >
                <v-icon left small>mdi-file-image-outline</v-icon>
                JPG
              </v-btn>
              <v-btn
                small
                color="deep-orange"
                class="download-action white--text mb-2"
                elevation="4"
                :loading="downloadingType === 'pdf'"
                :disabled="!!downloadingType"
                @click="downloadExamTimetable('pdf')"
              >
                <v-icon left small>mdi-file-pdf-box</v-icon>
                PDF
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-sheet>

      <v-alert v-if="error" type="error" dense outlined class="mb-4 feedback-alert feedback-alert--error">
        {{ $t("teach_table.error") }}
      </v-alert>

      <v-alert
        v-if="!loading && examRows.length === 0 && studentId && !error"
        type="info"
        dense
        outlined
        class="feedback-alert feedback-alert--info"
      >
        {{ $t("student_exam_timetable.no_exam") }}
      </v-alert>

      <div v-if="examRows.length > 0">
        <v-sheet ref="examScheduleExport" class="schedule-panel pa-4">
          <div class="exam-list">
            <div v-for="exam in examRows" :key="exam.key" class="exam-item">
              <div class="exam-date">
                <div class="exam-day">{{ formatExamDay(exam) }}</div>
                <div class="exam-time">{{ formatExamTime(exam) }}</div>
              </div>
              <v-divider
                class="exam-divider"
                :vertical="$vuetify.breakpoint.smAndUp"
              ></v-divider>
              <div class="exam-detail">
                <v-chip x-small class="exam-type-chip white--text" :color="exam.color">
                  {{ exam.typeLabel }}
                </v-chip>
                <div class="exam-course mt-2">
                  <span>{{ exam.subject_id }} </span>
                  <a class="subject-link" @click="openSubjectDialog(exam.subject_id)">
                    {{ exam.subjectName }}
                  </a>
                </div>
                <div class="exam-meta">
                  {{ $t("teach_table.section") }} {{ exam.section }} <lect-or-prac-chip :item="exam" />
                </div>
                <a
                  v-if="exam.seat"
                  class="subject-link exam-location d-inline-flex align-center mt-1"
                  @click="openSeatMap(exam)"
                >
                  <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                  {{ formatExamLocation(exam) }}
                </a>
                <div v-else class="exam-location mt-1">
                  <v-icon small color="grey">mdi-map-marker-outline</v-icon>
                  -
                </div>
                <div v-if="exam.progress" class="exam-progress mt-3">
                  <div class="exam-progress-row">
                    <v-progress-linear
                      :value="exam.progress.percent"
                      height="8"
                      rounded
                      :striped="exam.progress.status === 'upcoming'"
                      :color="exam.progress.color"
                      :background-color="exam.progress.backgroundColor"
                      class="exam-progress-linear"
                      :class="`exam-progress-linear--${exam.progress.status}`"
                    ></v-progress-linear>
                    <div
                      class="exam-progress-percent"
                      :class="`exam-progress-percent--${exam.progress.status}`"
                    >
                      {{ exam.progress.percent }}%
                    </div>
                  </div>
                  <div class="exam-progress-message mt-1">{{ exam.progress.message }}</div>
                </div>
              </div>
            </div>
          </div>
        </v-sheet>

        <v-sheet ref="examTableExport" class="table-panel mt-4">
          <v-simple-table dense class="subject-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center">{{ $t("teach_table.course_id") }}</th>
                  <th>{{ $t("teach_table.course_name") }}</th>
                  <th class="text-center">{{ $t("teach_table.section") }}</th>
                  <th class="text-center">{{ $t("student_exam_timetable.exam_type") }}</th>
                  <th>{{ $t("teach_table.exam_date") }}</th>
                  <th>{{ $t("student_exam_timetable.exam_location") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="exam in examRows" :key="`${exam.key}-table`">
                  <td class="text-center">{{ exam.subject_id }}</td>
                  <td>
                    <a class="subject-link" @click="openSubjectDialog(exam.subject_id)">
                      {{ exam.subjectName }}
                    </a>
                  </td>
                  <td class="text-center">{{ exam.section }}<div><lect-or-prac-chip :item="exam" /></div></td>
                  <td class="text-center">
                    <v-chip x-small class="white--text" :color="exam.color">
                      {{ exam.typeLabel }}
                    </v-chip>
                  </td>
                  <td>{{ formatExamDateTime(exam) }}</td>
                  <td>
                    <a
                      v-if="exam.seat"
                      class="subject-link d-inline-flex align-center"
                      @click="openSeatMap(exam)"
                    >
                      <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                      {{ formatExamLocation(exam) }}
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-sheet>
      </div>
      <subject-dialog ref="subject_dialog" :level-id="levelId" />
    </v-card-text>
  </v-card>
</template>

<script>
import * as dayjs from "dayjs";
require("dayjs/locale/th");
import RegistrationProvider from "@/services/registration.provider.js";
import UserCentralProvider from "@/services/user.central.provider.js";
import { parseDate_Time } from "@/helper/registration";
import { dateTimeFromDBTranslatorFull } from "@/helper/translator";
import LectOrPracChip from '@/views/teach_table/components/LectOrPracChip.vue';
import SubjectDialog from "@/views/teach_table/components/SubjectDialog.vue";
import {
  createPrintPage,
  renderExportElementToCanvas,
  saveJpgPages,
  savePdfPages,
} from "./timetableExport";

const RegistrationService = new RegistrationProvider();
const UserCentralService = new UserCentralProvider();

export default {
  name: "StudentExamTimetable",
  components: {
    SubjectDialog,
    LectOrPracChip,
  },
  data: () => ({
    loading: false,
    error: false,
    year: null,
    semester: null,
    currentYear: null,
    currentSemester: null,
    studentIdInput: null,
    selectedStudentInfo: null,
    selectedStudentInfoStudentId: null,
    subjects: [],
    updatedAt: null,
    registrationStartDate: null,
    downloadingType: null,
  }),
  computed: {
    selectedTerm: {
      get() {
        if (!this.year || !this.semester) return null;
        return `${this.year}-${this.semester}`;
      },
      set(value) {
        if (!value) {
          this.year = null;
          this.semester = null;
          return;
        }

        const [year, semester] = value.split("-");
        this.year = year;
        this.semester = semester;
      },
    },
    selectedTermLabel() {
      if (!this.year || !this.semester) return "-";
      return `${this.semester} / ${this.formatAcademicYear(this.year)}`;
    },
    formattedUpdatedAt() {
      return this.updatedAt ? dateTimeFromDBTranslatorFull(this.updatedAt) : "-";
    },
    studentId() {
      if (this.studentIdInput) return String(this.studentIdInput).trim();
      return this.$store.state.user.surrogated_student_id;
    },
    currentStudentInfo() {
      if (this.canEditStudentId && this.studentIdInput) {
        return this.selectedStudentInfoStudentId === this.studentId
          ? this.selectedStudentInfo
          : null;
      }
      return this.$store.state.user.surrogated_student_info;
    },
    studentName() {
      const studentInfo = this.currentStudentInfo;
      if (!studentInfo) return "-";
      const name =
        this.$i18n.locale === "en"
          ? [studentInfo.fname_en, studentInfo.lname_en]
          : [studentInfo.fname_th, studentInfo.lname_th];
      return name.filter(Boolean).join(" ") || "-";
    },
    exportMetadata() {
      return {
        title: this.$t("student_exam_timetable.title"),
        studentIdLabel: this.$t("student_exam_timetable.student_id"),
        studentId: this.studentId,
        studentNameLabel: this.$t("pre_regis_panel.name_surname"),
        studentName: this.studentName,
        termLabel: this.$t("student_exam_timetable.year_semester"),
        term: this.selectedTermLabel,
        updatedAtLabel: this.$t("student_exam_timetable.updated_at"),
        updatedAt: this.formattedUpdatedAt,
      };
    },
    canEditStudentId() {
      return this.$store.state.user.is_authorized_officer 
    },
    levelId() {
      const studentInfo = this.currentStudentInfo;
      const privilege =
        studentInfo &&
        studentInfo.payload &&
        studentInfo.payload.ticket &&
        studentInfo.payload.ticket.privilege;

      if (!privilege) return "1";
      return privilege == 80 ? "1" : "2";
    },
    termOptions() {
      const baseYear = Number(this.currentYear || this.year || dayjs().year() + 543);
      const options = [];
      for (let y = baseYear; y >= baseYear - 5; y -= 1) {
        ["3", "2", "1"].forEach((semester) => {
          options.push({
            label: `${semester} / ${this.formatAcademicYear(y)}`,
            value: `${y}-${semester}`,
          });
        });
      }
      return options;
    },
    examRows() {
      const rows = this.subjects
        .flatMap((subject) => {
          const rows = [];

          if (subject.midterm_start_date_time) {
            rows.push(this.buildExamRow(subject, "midterm"));
          }

          if (subject.final_start_date_time) {
            rows.push(this.buildExamRow(subject, "final"));
          } else if (subject.exam_date === "0000-00-00") {
            rows.push(this.buildExamRow(subject, "final", true));
          }

          return rows;
        })
        .sort((a, b) => {
          if (a.selfArranged && !b.selfArranged) return 1;
          if (!a.selfArranged && b.selfArranged) return -1;
          if (a.selfArranged && b.selfArranged) return a.subject_id.localeCompare(b.subject_id);
          if (a.start.isBefore(b.start)) return -1;
          if (a.start.isAfter(b.start)) return 1;
          return a.subject_id.localeCompare(b.subject_id);
        });

      return rows.map((exam) => ({
        ...exam,
        progress: exam.selfArranged
          ? null
          : this.buildExamProgress(exam.start),
      }));
    },
  },
  methods: {
    dayjs,
    formatAcademicYear(year) {
      const numericYear = Number(year);
      if (this.$i18n.locale === "en" && numericYear > 2400) return numericYear - 543;
      return year;
    },
    buildExamRow(subject, type, selfArranged = false) {
      const isMidterm = type === "midterm";
      const examPeriod = isMidterm ? "M" : "F";
      const seat = subject.exam_seats && subject.exam_seats[examPeriod]
        ? subject.exam_seats[examPeriod]
        : null;
      return {
        key: `${subject.rowKey}-${type}`,
        type,
        typeLabel: isMidterm ? "Midterm" : "Final",
        color: isMidterm ? "indigo" : "deep-orange",
        subject_id: subject.subject_id,
        subjectName: subject.subjectName,
        section: subject.section,
        lect_or_prac: subject.lect_or_prac,
        examPeriod,
        seat,
        start: selfArranged ? null : isMidterm ? subject.midterm_start_date_time : subject.final_start_date_time,
        end: selfArranged ? null : isMidterm ? subject.midterm_end_date_time : subject.final_end_date_time,
        selfArranged,
        progress: null,
      };
    },
    formatExamDay(exam) {
      if (exam.selfArranged) return this.$t("student_exam_timetable.self_arranged");
      const yearFormat = this.$i18n.locale === "th" ? "BBBB" : "YYYY";
      return exam.start.locale(this.$i18n.locale).format(`dddd D MMMM ${yearFormat}`);
    },
    formatExamTime(exam) {
      if (exam.selfArranged) return "";
      return `${exam.start.format("HH:mm")} - ${exam.end.format("HH:mm")}`;
    },
    formatExamDateTime(exam) {
      if (exam.selfArranged) return this.$t("student_exam_timetable.self_arranged");
      return `${this.formatExamDay(exam)} ${this.formatExamTime(exam)}`;
    },
    columnLabel(columnNumber) {
      let number = Number(columnNumber);
      let label = "";
      while (number > 0) {
        number -= 1;
        label = String.fromCharCode(65 + (number % 26)) + label;
        number = Math.floor(number / 26);
      }
      return label;
    },
    seatLabel(seat) {
      if (!seat || !seat.col || !seat.row) return "-";
      return `${this.columnLabel(seat.col)}${seat.row}`;
    },
    formatExamLocation(exam) {
      if (!exam.seat) return "-";
      return [
        exam.seat.building_name || "-",
        exam.seat.room_name || "-",
        this.seatLabel(exam.seat),
      ].join(" - ");
    },
    openSeatMap(exam) {
      if (!exam.seat) return;
      this.$router.push({
        name: "student_exam_timetable_seat",
        query: {
          student_id: this.studentId,
          year: this.year,
          semester: this.semester,
          level_id: this.levelId,
          subject_id: exam.subject_id,
          exam_period: exam.examPeriod,
        },
      });
    },
    buildExamProgress(examStart) {
      if (!examStart || !this.registrationStartDate) return null;

      const regisStart = dayjs(this.registrationStartDate, "YYYY-MM-DD HH:mm:ss");
      if (!regisStart.isValid()) return null;

      const now = dayjs();
      const totalMs = Math.max(examStart.diff(regisStart), 1);
      const elapsedMs = now.diff(regisStart);
      const isExamToday = now.isSame(examStart, "day");
      const isExamPassed = now.isAfter(examStart, "day");
      const status = isExamToday ? "today" : isExamPassed ? "passed" : "upcoming";
      const percent = isExamToday || isExamPassed
        ? 100
        : Math.max(0, Math.min(99, Math.floor((elapsedMs / totalMs) * 100)));

      return {
        percent,
        status,
        color: status === "today" ? "green darken-3" : status === "passed" ? "grey" : "deep-orange",
        backgroundColor: status === "today" ? "green lighten-5" : status === "passed" ? "grey lighten-3" : "deep-orange lighten-5",
        message: isExamToday
          ? this.examTodayText()
          : isExamPassed
            ? this.examPassedText()
          : this.examRemainingText(examStart),
      };
    },
    examRemainingText(targetDate) {
      const now = dayjs();
      const days = Math.max(targetDate.startOf("day").diff(now.startOf("day"), "day"), 0);
      if (this.$i18n.locale === "en") {
        return `${days} day${days === 1 ? "" : "s"} remaining until this exam`;
      }
      return `เหลืออีก ${days} วันก่อนถึงวันสอบวิชานี้`;
    },
    examTodayText() {
      if (this.$i18n.locale === "en") {
        return "This exam is today";
      }
      return "ถึงวันสอบวิชานี้แล้ว";
    },
    examPassedText() {
      if (this.$i18n.locale === "en") {
        return "This exam has already passed";
      }
      return "ผ่านวันสอบวิชานี้มาแล้ว";
    },
    openSubjectDialog(subjectId) {
      if (this.$refs.subject_dialog) {
        this.$refs.subject_dialog.show_item_detail(subjectId);
      }
    },
    async downloadExamTimetable(type) {
      if (!this.$refs.examScheduleExport || !this.$refs.examTableExport) return;

      try {
        this.downloadingType = type;
        await this.$nextTick();

        const pages = await this.buildExamExportPages();
        const baseName = this.examExportFileBaseName();

        if (type === "pdf") savePdfPages(pages, baseName);
        else await saveJpgPages(pages, baseName);
      } catch (error) {
        if (this.$toasted) {
          this.$toasted.error(error.message || "Download failed", { duration: 3000 });
        }
      } finally {
        this.downloadingType = null;
      }
    },
    async buildExamExportPages() {
      const scheduleCanvas = await renderExportElementToCanvas(
        this.$refs.examScheduleExport,
        this.exportMetadata,
        {
          orientation: "portrait",
        }
      );
      const tableCanvas = await renderExportElementToCanvas(
        this.$refs.examTableExport,
        this.exportMetadata,
        {
          orientation: "landscape",
        }
      );

      return [
        {
          canvas: createPrintPage(scheduleCanvas, "portrait"),
          orientation: "portrait",
          fileSuffix: "page-1-schedule",
        },
        {
          canvas: createPrintPage(tableCanvas, "landscape"),
          orientation: "landscape",
          fileSuffix: "page-2-table",
        },
      ];
    },
    examExportFileBaseName() {
      return [
        "student-exam-timetable",
        this.studentId || "student",
        this.year || "year",
        this.semester || "semester",
      ].join("-");
    },
    normalizeExamDate(date) {
      if (!date || date === "0000-00-00") return date;
      const year = Number(String(date).substring(0, 4));
      if (year > 2400) return `${year - 543}${String(date).substring(4)}`;
      return date;
    },
    normalizeExamDateFields(item) {
      return {
        ...item,
        mexam_date: this.normalizeExamDate(item.mexam_date),
        exam_date: this.normalizeExamDate(item.exam_date),
      };
    },
    normalizeSubject(item, index) {
      const parsed = parseDate_Time(this.normalizeExamDateFields(item));
      const subjectName =
        parsed.subject_ename ||
        parsed.subject_tname ||
        parsed.subject_name_en ||
        parsed.subject_name_th ||
        "";

      return {
        ...parsed,
        rowKey: `${parsed.teach_table_id || parsed.subject_id}-${parsed.section}-${index}`,
        subjectName,
      };
    },
    async getCurrentTerm() {
      const data = await RegistrationService.getYearSemesterNow(this.levelId);
      this.currentYear = data.YEAR;
      this.currentSemester = data.SEMESTER;
      if (!this.year) this.year = data.YEAR;
      if (!this.semester) this.semester = data.SEMESTER;
    },
    async ensureSelectedStudentInfo() {
      if (!this.canEditStudentId || !this.studentIdInput) return;

      const studentId = String(this.studentIdInput).trim();
      if (
        this.selectedStudentInfo &&
        this.selectedStudentInfoStudentId === studentId
      ) {
        return;
      }

      const studentInfo = await UserCentralService.getAuthUserInfo(studentId);
      if (studentId !== String(this.studentIdInput || "").trim()) return;

      this.selectedStudentInfo = studentInfo;
      this.selectedStudentInfoStudentId = studentId;
    },
    async getRegistrationDateTime() {
      const data = await RegistrationService.checkRegistrationDateTime({
        level_id: this.levelId,
        year: this.year,
        semester: this.semester,
      });
      this.registrationStartDate = data && data.regis ? data.regis.start_date : null;
    },
    syncQuery() {
      this.$router
        .replace({
          name: "student_exam_timetable",
          query: {
            ...this.$route.query,
            student_id: this.studentId,
            year: this.year,
            semester: this.semester,
            level_id: this.levelId,
          },
        })
        .catch(() => {});
    },
    onTermChange() {
      this.fetchData();
    },
    async fetchData() {
      if (!this.studentId || !this.year || !this.semester) return;

      try {
        this.loading = true;
        this.error = false;
        await this.ensureSelectedStudentInfo();
        const data = await RegistrationService.getRegisResult({
          student_id: this.studentId,
          year: this.year,
          semester: this.semester,
          level_id: this.levelId,
        });
        this.subjects = data.map((item, index) => this.normalizeSubject(item, index));
        await this.getRegistrationDateTime();
        this.updatedAt = dayjs().format("YYYY-MM-DD HH:mm:ss");
        this.syncQuery();
      } catch (error) {
        this.error = true;
        this.subjects = [];
        if (error.response && error.response.data && error.response.data.message) {
          this.$toasted.error(JSON.stringify(error.response.data.message), {
            duration: 0,
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchInit() {
      this.studentIdInput = this.$route.query.student_id || null;
      this.year = this.$route.query.year || null;
      this.semester = this.$route.query.semester || null;
      await this.ensureSelectedStudentInfo().catch(() => {});
      await this.getCurrentTerm();
      await this.fetchData();
    },
  },
  created() {
    this.fetchInit();
  },
};
</script>

<style scoped>
.student-exam-timetable {
  max-width: 1440px;
  margin: 0 auto;
  background: #fffdfb;
}

.hero-panel {
  background: #fff3ea;
  border-bottom: 1px solid rgba(224, 182, 146, 0.42);
}

.hero-header {
  gap: 16px;
  padding: 28px 32px 24px;
}

.hero-copy {
  max-width: 680px;
}

.hero-title {
  color: #5d4036;
  font-family: "Anuphan", sans-serif !important;
  letter-spacing: 0;
}

.hero-subtitle {
  color: #87685d;
  max-width: 54ch;
}

.content-shell {
  padding: 24px;
}

.filter-panel,
.schedule-panel,
.table-panel {
  overflow: hidden;
  border: 1px solid #f1e1d6;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 16px 30px rgba(171, 126, 97, 0.08);
}

.filter-panel {
  background: #fff8f4;
}

.soft-field {
  border-radius: 10px;
}

.search-btn {
  width: 100%;
  max-width: 100px;
  letter-spacing: 0.01em;
}

.meta-chip {
  font-weight: 500;
}

.updated-at-col {
  margin-left: auto;
}

.download-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-left: auto;
}

.download-action {
  box-shadow: 0 6px 14px rgba(216, 91, 43, 0.18) !important;
}

.feedback-alert {
  border-radius: 10px;
}

.feedback-alert--error {
  background: #fff4f2 !important;
}

.feedback-alert--info {
  background: #f8fbff !important;
}

.exam-progress-panel {
  border: 1px solid #f1e1d6;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 16px 30px rgba(171, 126, 97, 0.08);
}

.exam-progress-percent {
  flex: 0 0 34px;
  color: #e64a19;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  text-align: right;
}

.exam-progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.exam-progress-percent--today {
  color: #2e7d32;
}

.exam-progress-percent--passed {
  color: #757575;
}

.exam-progress-message {
  color: #7b675d;
  font-size: 13px;
  font-weight: 600;
}

.exam-progress-linear {
  flex: 1 1 auto;
  overflow: hidden;
}

.exam-progress-linear--upcoming ::v-deep .v-progress-linear__determinate {
  background-size: 28px 28px;
  animation: exam-progress-stripes 1.2s linear infinite;
}

.exam-progress-linear--today {
  animation: exam-progress-glow 2.4s ease-in-out infinite;
}

.exam-progress-linear--today ::v-deep .v-progress-linear__determinate,
.exam-progress-linear--passed ::v-deep .v-progress-linear__determinate {
  background-image: none !important;
}

@keyframes exam-progress-stripes {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 28px 0;
  }
}

@keyframes exam-progress-glow {
  0%,
  100% {
    box-shadow: 0 0 6px rgba(46, 125, 50, 0.28);
  }
  50% {
    box-shadow: 0 0 18px rgba(46, 125, 50, 0.86);
  }
}

.exam-list {
  display: grid;
  gap: 12px;
}

.exam-item {
  display: grid;
  grid-template-columns: 220px 1px minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
  padding: 16px;
  border: 1px solid #f3e6dc;
  border-radius: 10px;
  background: #fffdfb;
}

.exam-date {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.exam-day {
  color: #5d4036;
  font-size: 15px;
  font-weight: 800;
}

.exam-time {
  margin-top: 4px;
  color: #7b675d;
  font-size: 13px;
  font-weight: 700;
}

.exam-divider {
  align-self: stretch;
  border-color: #d7d7d7 !important;
}

.exam-detail {
  min-width: 0;
}

.exam-type-chip {
  font-weight: 700;
}

.exam-course {
  color: #5b4337;
  font-size: 15px;
  font-weight: 800;
  word-break: break-word;
}

.subject-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: none;
  font-weight: inherit;
}

.subject-link:hover {
  text-decoration: none;
}

.exam-location {
  color: #1976d2;
  font-size: 13px;
  font-weight: 400;
}

.exam-meta {
  margin-top: 4px;
  color: #7b675d;
  font-size: 13px;
  font-weight: 600;
}

.subject-table {
  background: transparent;
}

.subject-table thead {
  background: #fdf0e1;
}

.subject-table th {
  color: #5d4036 !important;
  font-family: "Sarabun", sans-serif !important;
  font-size: 12px !important;
  letter-spacing: 0;
  text-transform: uppercase;
  border-bottom: none !important;
}

.subject-table td {
  border-color: #f3e6dc !important;
  padding: 4px 8px !important;
}

.subject-table tbody tr:nth-child(even) {
  background: #fffaf6;
}

.subject-table tbody tr:hover {
  background: #fff3ea;
}

@media (max-width: 960px) {
  .hero-header {
    padding: 24px 20px 20px;
  }

  .content-shell {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .student-exam-timetable,
  .filter-panel,
  .schedule-panel,
  .table-panel {
    border-radius: 10px;
  }

  .content-shell {
    padding: 16px;
  }

  .search-btn {
    max-width: none;
  }

  .download-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }

  .exam-item {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .exam-divider {
    width: 100%;
    max-width: none;
  }
}
</style>
